import React, {
  useState,
  useEffect,
  useCallback,
  useRef,
  useMemo
} from 'react';
import { createPortal } from 'react-dom';
import { Field } from '@input';
import Arrow from './arrow.svg';
import ArrowBold from './arrow-bold.svg';
import useClickOutside from '@hooks/useClickOutside';
import useEvent from '@hooks/useEvent';
import useHasRendered from '@hooks/useHasRendered';
import * as styles from './Select.module.scss';
import classNames from 'classnames';
import { uniqueId, isNumber } from 'lodash';
import { isSSR } from '@helpers';

export default function Select({
  options = [],
  id: _id,
  value,
  name,
  label,
  inlineLabel,
  placeholder,
  isDisabled,
  optionsMinWidth,
  maxWidth,
  className,
  fieldClassName,
  fieldStyle,
  tabIndex = 0,
  usePortal = true,
  theme,
  offsetEmptyLeft,
  offsetEmptyRight,
  hasError,
  errorMessage,
  showErrorMessage,
  onChange,
  onFocus,
  onBlur,
  onKeyDown,
  ...restProps
}) {
  const id = useMemo(() => _id ?? uniqueId('select-'), [_id]);
  const errorId = useMemo(() => uniqueId('select-error-'), []);
  const listId = useMemo(() => uniqueId('list-'), []);
  const labelId = useMemo(() => uniqueId('label-'), []);
  const [isOpen, setIsOpen] = useState(false);
  const isOpenRef = useRef(false);
  const ref = useRef(null);
  const [rect, setRect] = useState(null);
  const valueRef = useRef('');

  useEffect(() => {
    valueRef.current = value;
  });

  const refreshRect = useCallback(usePortal => {
    let { left, top, width, height } = ref.current.getBoundingClientRect();

    if (usePortal) {
      left += window.pageXOffset;
      top += window.pageYOffset;
    }

    setRect({ left, top, width, height });
  }, []);

  useEffect(() => {
    refreshRect(usePortal);
  }, [usePortal, refreshRect]);

  const open = useCallback(() => {
    isOpenRef.current = true;

    refreshRect(usePortal);

    if (!isDisabled) {
      setIsOpen(true);
    }
  }, [usePortal, isDisabled, refreshRect]);

  const close = useCallback(() => {
    isOpenRef.current = false;
    setIsOpen(false);
    ref.current.blur();
  }, []);

  useEvent('visibilitychange', close);
  useEvent('blur', 'window', () => {
    ref.current.blur();
    close();
  });

  const handleKeyDown = useCallback(
    e => {
      if (e.key === 'Tab') {
        close();
      }

      if (isOpenRef.current && (e.key === 'Esc' || e.key === 'Escape')) {
        e.stopPropagation();
        close();
      }

      if (onKeyDown) {
        onKeyDown(valueRef.current, name, e);
      }
    },
    [close, name, onKeyDown]
  );

  const handleChange = useCallback(
    value => {
      valueRef.current = value;
      onChange(value, name);
      close(value);
    },
    [name, onChange, close]
  );

  const valueLabel = options.find(v => v.value === value)?.label;
  const isMinimal = theme === 'minimal';
  const ArrowComponent = isMinimal ? ArrowBold : Arrow;

  useEffect(() => {
    if (isDisabled && isOpenRef.current) {
      close();
    }
  }, [isDisabled, close]);

  const hasVisibleError = errorMessage && showErrorMessage;

  // aria-invalid={errorMessage && showErrorMessage ? true : false}

  return (
    <Field
      inputId={id}
      label={label}
      labelId={labelId}
      className={classNames(fieldClassName, {
        [styles.offsetEmptyLeft]: isMinimal && offsetEmptyLeft,
        [styles.offsetEmptyRight]: isMinimal && offsetEmptyRight
      })}
      style={fieldStyle}
      maxWidth={maxWidth}
      errorId={errorId}
      errorMessage={errorMessage}
      showErrorMessage={showErrorMessage}
    >
      <button
        id={id}
        ref={ref}
        aria-labelledby={inlineLabel && !label ? null : labelId}
        aria-haspopup="listbox"
        aria-owns={listId}
        aria-expanded={isOpen}
        className={classNames(
          'calculator-select-input',
          styles.wrapper,
          className,
          {
            [styles.focus]: isOpen,
            [styles.hasError]: hasError,
            [styles.minimal]: isMinimal,
            [styles.disabled]: isDisabled
          }
        )}
        tabIndex={tabIndex}
        onClick={() => {
          ref.current.focus();
        }}
        onFocus={() => {
          if (!isOpen) {
            open();
          }
        }}
        onBlur={() => {
          if (onBlur) {
            onBlur(valueRef.current, name);
          }
        }}
        onKeyDown={handleKeyDown}
        {...restProps}
      >
        <span className={styles.text}>
          {inlineLabel && (
            <span
              id={labelId}
              className={classNames('select-inline-label', styles.inlineLabel)}
            >
              {inlineLabel + ' '}
            </span>
          )}
          {valueLabel ? (
            <span className={styles.value}>{valueLabel}</span>
          ) : (
            <>
              {placeholder && (
                <span className={styles.placeholder}>{placeholder}</span>
              )}
            </>
          )}
        </span>

        <span className={styles.arrowWrapper}>
          <ArrowComponent className={styles.arrow} />
        </span>
      </button>
      <OptionsWrapper
        id={listId}
        isOpen={isOpen}
        inputRect={rect}
        optionsMinWidth={optionsMinWidth}
        options={options}
        value={value}
        usePortal={usePortal}
        // hasVisibleError
        close={close}
        onChange={handleChange}
        aria-invalid={hasVisibleError}
        aria-errormessage={hasVisibleError ? errorId : null}
      />
    </Field>
  );
}

function OptionsWrapper({
  id,
  isOpen,
  inputRect,
  optionsMinWidth,
  options = [],
  value: currentValue,
  usePortal,
  close,
  onChange,
  ...rest
}) {
  const ref = useRef(null);
  const hasRendered = useHasRendered();
  const [focusedIndex, setFocusedIndex] = useState(() => {
    const index = options.findIndex(({ value }) => {
      return value === currentValue;
    });

    return index === -1 ? 0 : index;
  });

  useClickOutside(ref, () => close(currentValue));

  const handleKeyDown = useCallback(
    e => {
      if (!isOpen) {
        return;
      }

      // Select on enter key
      if (e.key === 'Enter' && focusedIndex !== null) {
        onChange(options[focusedIndex].value);
        return;
      }

      // Keyboard navigation with up and down arrows
      const isUp = ['ArrowUp', 'Up'].includes(e.key);
      const isDown = !isUp && ['ArrowDown', 'Down'].includes(e.key);

      if (isUp || isDown) {
        e.preventDefault();
        setFocusedIndex(index => {
          if (index === null) {
            return isUp ? options.length - 1 : 0;
          }

          index = isUp ? index - 1 : index + 1;

          if (index >= options.length) {
            index = 0;
          } else if (index < 0) {
            index = options.length - 1;
          }

          return index;
        });

        return;
      }

      // Close on tab out
      if (e.key === 'Tab') {
        close();
      }
    },
    [isOpen, options, focusedIndex, close, onChange]
  );

  useEvent('keydown', handleKeyDown);

  const selectOptions = useMemo(() => {
    return options.map(option => ({
      id: uniqueId('option-'),
      ...option
    }));
  }, [options]);

  const [optionComponents, focusedId] = useMemo(() => {
    let focusedId = selectOptions[focusedIndex].id;

    const items = selectOptions.map(({ value, label, info, id }, index) => {
      const isCurrent = value === currentValue;
      const isFocused = focusedIndex === index;

      if (isFocused) {
        focusedId = id;
      }

      return (
        <Option
          key={value}
          id={id}
          label={label}
          value={value}
          info={info}
          isCurrent={isCurrent}
          isFocused={isFocused}
          onChange={onChange}
        />
      );
    });

    return [items, focusedId];
  }, [selectOptions, currentValue, focusedIndex, onChange]);

  const { isAbove, style } = useMemo(() => {
    if (isSSR()) {
      return { isAbove: false, style: null };
    }

    const {
      left = 0,
      top = 0,
      width: inputWidth = 0,
      height = 0
    } = inputRect ?? {};
    const minWidth = isNumber(optionsMinWidth)
      ? Math.max(inputWidth, optionsMinWidth)
      : inputWidth;
    const width = !hasRendered ? inputWidth : minWidth;
    const wrapperHeight = ref.current?.offsetHeight ?? 0;
    const minOffset = 25;

    let offset = top + height + wrapperHeight + minOffset;

    if (usePortal) {
      offset -= window.scrollY;
    }

    const isAbove = offset > window.innerHeight;

    return {
      isAbove,
      style: {
        left: usePortal ? left : null,
        top: usePortal ? top : null,
        marginTop: isAbove ? (usePortal ? -6 : -height - 6) : null,
        minWidth: width,
        maxWidth: width + 30
      }
    };
  }, [inputRect, optionsMinWidth, usePortal, hasRendered]);

  useEffect(() => {
    if (!isOpen) {
      ref.current?.scrollTo(0, 0);
    }
  }, [isOpen]);

  const el = (
    <ul
      ref={ref}
      id={id}
      role="listbox"
      aria-invalid={true}
      tabIndex="-1"
      aria-activedescendant={focusedId}
      className={classNames(styles.optionsWrapper, {
        [styles.open]: isOpen,
        [styles.inline]: !usePortal,
        [styles.above]: isAbove
      })}
      style={style}
      {...rest}
    >
      {optionComponents}
    </ul>
  );

  return !usePortal || isSSR() ? el : createPortal(el, document.body);
}

const Option = React.memo(
  ({ label, value, info, isCurrent, isFocused, onChange, id, ...rest }) => {
    return (
      <li
        id={id}
        role="option"
        aria-selected={isCurrent}
        tabIndex="-1"
        className={classNames(styles.option, {
          [styles.current]: isCurrent,
          [styles.focus]: isFocused
        })}
        onClick={() => onChange(value)}
        {...rest}
      >
        <span>{label}</span>
        {info && <span className={styles.info}>{info}</span>}
      </li>
    );
  }
);
